import type React from 'react';

export enum SkeletonLoaderVariant {
  BARS = 'three-bar',
  GRID = 'grid',
  LIST = 'list',
  CARD = 'card',
  TEXT_BLOCK = 'text-block',
}

export interface BaseSkeletonLoaderProps {
  isAnimated?: boolean;
  isPulsating?: boolean;
  className?: string;
  variant?: SkeletonLoaderVariant;
}

export interface BarOptions {
  barThickness?: number;
  barWidths?: (string | number)[];
  barCount?: number;
}

export interface GridOptions {
  itemCount?: number;
  columns?: number;
  gap?: number;

  /**
   * If not provided, grid items will be square (aspect-ratio: 1).
   */
  gridItemHeight?: string;

  /**
   * Width for each grid item. Defaults to 100% of the grid cell.
   */
  gridItemWidth?: string | number;
}

export interface ListOptions {
  itemCount?: number;
  gap?: number;
}

export type SkeletonLoaderProps =
  | ({variant: SkeletonLoaderVariant.BARS; options?: BarOptions} & BaseSkeletonLoaderProps)
  | ({variant: SkeletonLoaderVariant.GRID; options?: GridOptions} & BaseSkeletonLoaderProps)
  | ({variant: SkeletonLoaderVariant.LIST; options?: ListOptions} & BaseSkeletonLoaderProps)
  | ({variant: SkeletonLoaderVariant.CARD; options?: never} & BaseSkeletonLoaderProps)
  | ({variant: SkeletonLoaderVariant.TEXT_BLOCK; options?: never} & BaseSkeletonLoaderProps);

interface SkeletonLoaderOptionsMap {
  [SkeletonLoaderVariant.BARS]: BarOptions;
  [SkeletonLoaderVariant.GRID]: GridOptions;
  [SkeletonLoaderVariant.LIST]: ListOptions;
  [SkeletonLoaderVariant.CARD]: Record<string, never>;
  [SkeletonLoaderVariant.TEXT_BLOCK]: Record<string, never>;
}

export const SKELETON_DEFAULTS = {
  [SkeletonLoaderVariant.BARS]: {
    barThickness: 12,
    barWidths: ['40%', '65%', '90%'],
    barCount: 3,
  } as BarOptions,

  [SkeletonLoaderVariant.GRID]: {
    itemCount: 6,
    columns: 3,
    gap: 16,
    gridItemHeight: 'auto',
    gridItemWidth: '100%',
  } as GridOptions,

  [SkeletonLoaderVariant.LIST]: {
    itemCount: 5,
    gap: 12,
  } as ListOptions,

  [SkeletonLoaderVariant.CARD]: {},
  [SkeletonLoaderVariant.TEXT_BLOCK]: {},
};

interface SkeletonCSSProperties extends React.CSSProperties {
  '--delay'?: string;
}

export interface BarCSSProperties extends SkeletonCSSProperties {
  '--bar-thickness'?: string;
}

export interface GridCSSProperties extends SkeletonCSSProperties {
  '--grid-columns'?: number;
  '--grid-gap'?: string;
  '--grid-item-height'?: string | undefined;
  '--grid-item-width'?: string | undefined;
  '--grid-aspect-ratio'?: string;
}

export interface ListCSSProperties extends SkeletonCSSProperties {
  '--list-gap'?: string;
}

export function getResolvedOptions<V extends SkeletonLoaderVariant>(variant: V, providedOptions?: SkeletonLoaderOptionsMap[V]): Required<SkeletonLoaderOptionsMap[V]> {
  const defaults = SKELETON_DEFAULTS[variant] as Required<SkeletonLoaderOptionsMap[V]>;

  return {...defaults, ...(providedOptions ?? {})};
}
